import React from 'react'
import type { Document } from '@contentful/rich-text-types'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import type { Options, RenderText } from '@contentful/rich-text-react-renderer'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { textStyles } from '@marketing-sites/ui'
import type {
  Maybe,
  RichTextBlockEntryFragment,
  RichTextHyperlinkToEntryFragment,
  RichTextInlineEntryFragment,
} from '../../../.graphql/generated-types'
import { notEmpty } from '@marketing-sites/utils'
import { RichTextHyperlinkToEntry } from '../RichTextHyperlinkToEntry'
import { RichTextBlockEntry } from '../RichTexBlockEntry'
import { RichTextInlineEntry } from '../RichTextInlineEntry'
import { InlineHyperlink } from '../InlineHyperlink'

export type Substitutions = Record<string, string>

const leftSnippetTokenDelimiter = '['
const rightSnippetTokenDelimiter = ']'

const snippetTokenFromKeyword = (snippetKeyword: string) =>
  `${leftSnippetTokenDelimiter}${snippetKeyword}${rightSnippetTokenDelimiter}`

/**
 * Given a Substitutions dictionary of snippet keywords to snippet texts, this
 * replaces all snippet tokens found within a given text with corresponding snippet texts.
 * Note: a snippet keyword is a string (eg 'some token') but a snippet token for that keyword
 * has snippet token delimiters wrapped around it. (eg '[some token]')
 */
function makeSnippetSubstitutions(
  substitutions: Substitutions,
  text: string,
): string {
  return Object.entries(substitutions).reduce(
    (resultantText, [snippetKeyword, snippetText]) => {
      const snippetToken = snippetTokenFromKeyword(snippetKeyword)
      return resultantText.replaceAll(snippetToken, snippetText)
    },
    text,
  )
}

function buildRenderText(substitutions: Substitutions): RenderText {
  return (text) =>
    typeof text === 'string'
      ? makeSnippetSubstitutions(substitutions, text)
      : text
}

const defaultOptions: Options = {
  renderMark: {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.CODE]: (text) => (
      <code className="inline-block bg-gray-200 p-6">{text}</code>
    ),
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (_node, children) => (
      <h1 className={textStyles.h1}>{children}</h1>
    ),
    [BLOCKS.HEADING_2]: (_node, children) => (
      <h2 className={textStyles.h2}>{children}</h2>
    ),
    [BLOCKS.HEADING_3]: (_node, children) => (
      <h3 className={textStyles.h3}>{children}</h3>
    ),
    [BLOCKS.HEADING_4]: (_node, children) => (
      <h4 className={textStyles.h3}>{children}</h4>
    ),
    [BLOCKS.HEADING_5]: (_node, children) => (
      <h5 className={textStyles.h3}>{children}</h5>
    ),
    [BLOCKS.HEADING_6]: (_node, children) => (
      <h6 className={textStyles.h3}>{children}</h6>
    ),
    [BLOCKS.PARAGRAPH]: (_node, children) => <p>{children}</p>,
    [BLOCKS.UL_LIST]: (_node, children) => (
      <ul className="ml-6 list-disc">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_node, children) => (
      <ol className="ml-6 list-decimal">{children}</ol>
    ),
    [BLOCKS.QUOTE]: (_node, children) => (
      <blockquote className="border-l-4 pl-6">{children}</blockquote>
    ),
    [INLINES.HYPERLINK]: (node, children) => (
      <InlineHyperlink href={(node.data['uri'] as Maybe<string>) ?? ''}>
        {children}
      </InlineHyperlink>
    ),
  },
}

interface EntryHyperlinkNode {
  data?: {
    target?: {
      sys?: {
        id?: string
      }
    }
  }
}

interface RawRichTextEntries {
  block?: Maybe<RichTextBlockEntryFragment>[] | undefined
  hyperlink?: Maybe<RichTextHyperlinkToEntryFragment>[]
  inline?: Maybe<RichTextInlineEntryFragment>[]
}

interface ReferencedEntries {
  block: RichTextBlockEntryFragment[]
  hyperlink: RichTextHyperlinkToEntryFragment[]
  inline: RichTextInlineEntryFragment[]
}

export function getReferencedEntries(
  entries: Maybe<RawRichTextEntries>,
): ReferencedEntries {
  return {
    block: (entries?.block ?? []).filter(notEmpty),
    inline: (entries?.inline ?? []).filter(notEmpty),
    hyperlink: (entries?.hyperlink ?? []).filter(notEmpty),
  }
}

interface CreateRenderOptionsProps {
  entries: Maybe<RawRichTextEntries>
  substitutions: Record<string, string>
}

function createRenderOptions({
  entries,
  substitutions,
}: CreateRenderOptionsProps): Options {
  const referencedEntries = getReferencedEntries(entries)
  const renderText = buildRenderText(substitutions)
  const renderOptions: Options = {
    ...defaultOptions,
    renderNode: {
      ...defaultOptions.renderNode,
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        const id =
          (node as unknown as EntryHyperlinkNode).data?.target?.sys?.id ?? ''
        const entry = referencedEntries.hyperlink.find((o) => o.sys.id === id)
        if (entry) {
          return (
            <RichTextHyperlinkToEntry data={entry}>
              {children}
            </RichTextHyperlinkToEntry>
          )
        }
        return children
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
        const id =
          (node as unknown as EntryHyperlinkNode).data?.target?.sys?.id ?? ''
        const entry = referencedEntries.block.find((o) => o.sys.id === id)
        if (entry) {
          return <RichTextBlockEntry data={entry} />
        }
        return children
      },
      [INLINES.EMBEDDED_ENTRY]: (node, children) => {
        const id =
          (node as unknown as EntryHyperlinkNode).data?.target?.sys?.id ?? ''
        const entry = referencedEntries.inline.find((o) => o.sys.id === id)

        if (entry) {
          return <RichTextInlineEntry data={entry} />
        }
        return children
      },
    },
    renderText,
  }
  return renderOptions
}

export interface ContentfulRichTextProps {
  document: Document
  entries?: Maybe<RawRichTextEntries> | undefined
  className?: string
  substitutions?: Substitutions
}

/**
 * Component used by other components to render Contentful RichText data
 * as React components. This RichText data can have embedded Contentful
 * data that will recursively render.
 */
export function ContentfulRichText({
  document,
  entries,
  className,
  substitutions,
}: ContentfulRichTextProps) {
  const options = createRenderOptions({
    entries: entries ?? {},
    substitutions: substitutions ?? {},
  })
  const result = documentToReactComponents(document, options)
  return <div className={className ?? ''}>{result}</div>
}
